import { useNavigate } from 'react-router-dom';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { GoodCatalogueTree } from '../../../presentation-components/organisation-components/good/good-catalogue-tree';
import GoodCategoryService from '../../../../shared/services/good-category/good-category.service';
import CapitalGoodService from '../../../../shared/services/capital-good/capital-good-service';
import { useContext, useEffect, useState } from 'react';
import { ICapitalGood } from '../../../../shared/services/capital-good/capital-good.model';
import { InformationBox } from '../../../presentation-components/miscellaneous-components/information-box';
import { Box, Grid, InputAdornment, Stack, TablePagination, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { GridView, MenuOutlined, SearchOutlined } from '@mui/icons-material';
import { ProductCatalogueCard, productFootprintLabel } from '../../../presentation-components/organisation-components/good/product-catalogue-card';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';

/**
 * One register, two pages: purchased & capital goods, and products the organisation
 * sells. Split from a single page with a type toggle so each gets its own nav entry,
 * heading and empty states.
 */
export const OrganisationGoodsPage = ({ mode }: { mode: 'purchased' | 'sold' }) => {
  const navigate = useNavigate();
  //Services
  const goodCategoryService = new GoodCategoryService();
  const capitalGoodService = new CapitalGoodService()
  //Context
  const userDetailsContext = useContext(UserDetailsContext)
  const userDetails = userDetailsContext.userDetails
  const screenLabelsContext = useContext(ScreenLabelsContext)
  //State
  const [hasNoGoodCategories, setHasNoGoodCategories] = useState<boolean>(false)
  const [goodCatalogue, setGoodCatalogue] = useState<any>()
  const [capitalGoodsInCategory, setCapitalGoodsInCategory] = useState<ICapitalGood[]>()
  const [selectedCatalogueItemId, setSelectedCatalogueItemId] = useState<string | null>(null);
  const [view, setView] = useState<'cards' | 'table'>('cards')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTableRows, setSelectedTableRows] = useState<string[]>([])
  //Table State
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(12)

  // The backend hides sold products from goods lists by default (so emission pickers never
  // see them); the sold page is the one place that opts back in.
  const typeFilterQuery = mode === 'sold' ? '&typeOfGood=soldProduct' : ''

  // The tree's composite item id is 'category-subCategoryOne-subCategoryTwo'; keeping the full
  // id means pagination and filter changes stay inside the selected subcategory (and the tree
  // highlight stays put) instead of silently widening back to the top-level category.
  const categoryQuery = (itemId: string | null) => {
    if (!itemId) return ''
    const parts = itemId.split('-')
    return `&goodCategory=true&category=${encodeURIComponent(parts[0])}${parts[1] ? `&subCategoryOne=${encodeURIComponent(parts[1])}${parts[2] ? `&subCategoryTwo=${encodeURIComponent(parts[2])}` : ''}` : ''}`
  }

  const fetchGoods = (itemId: string | null, page: number, search: string = searchTerm) => {
    capitalGoodService.getAllActiveWithPagination(`${categoryQuery(itemId)}${typeFilterQuery}${search.trim() ? `&nameIncludes=${encodeURIComponent(search.trim())}` : ''}`, page, perPage)
      .then(response => {
        setCapitalGoodsInCategory(response.rows)
        setTotalRecords(response.count)
        setInputPage(page)
      })
      .catch(err => console.log(err))
  }

  useEffect(() => {
    goodCategoryService.getAll()
      .then(response => {
        if (response.length === 0) {
          setHasNoGoodCategories(true)
        }
      })
      .catch(err => console.log(err))

    goodCategoryService.getAllForCatalogue()
      .then(response => {
        setGoodCatalogue(response)
        const defaultSelectedId = response[0].id;
        setSelectedCatalogueItemId(defaultSelectedId);
        fetchGoods(defaultSelectedId, 0)
      })
      .catch(err => console.log(err))
    // mode is a dep because both split pages render this component: React keeps the
    // instance alive across the route switch, so the data must reload for the new mode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  const handleItemSelectionToggle = (event: React.SyntheticEvent | null, itemId: string, isSelected: boolean,) => {
    if (isSelected) {
      setSelectedCatalogueItemId(itemId)
      fetchGoods(itemId, 0)
    }
  };

  const handleGoodsPageChange = (_: unknown, newPage: number) => {
    fetchGoods(selectedCatalogueItemId, newPage)
  }

  // Debounced name search; resets to the first page of the current category and filter.
  useEffect(() => {
    if (!goodCatalogue) return
    const timer = setTimeout(() => fetchGoods(selectedCatalogueItemId, 0, searchTerm), 350)
    return () => clearTimeout(timer)
  }, [searchTerm]) // eslint-disable-line react-hooks/exhaustive-deps

  const tableHeadCells: IHeadCell[] = [
    { id: 'name', numeric: false, label: screenLabelsContext.getLabel('product', { capitalize: 'first' }), sortable: false },
    { id: 'manufacturer', numeric: false, label: 'Manufacturer', sortable: false },
    { id: 'model', numeric: false, label: 'Model', sortable: false },
    { id: 'footprint', numeric: false, label: 'Footprint', sortable: false },
  ]
  const tableRows = (capitalGoodsInCategory ?? []).map(good => ({
    id: good.id,
    name: good.name,
    manufacturer: good.manufacturer?.name ?? '',
    model: good.modelNumber ?? '',
    footprint: productFootprintLabel(good) ?? 'No footprint yet',
  }))



  // Custom "product" screen labels fold into the split titles, matching the nav entries.
  const productsLabel = screenLabelsContext.getLabel('product', { plural: true, capitalize: 'first' })
  const hasCustomLabel = !!productsLabel && productsLabel !== 'Products'
  const pageTitle = mode === 'sold'
    ? (hasCustomLabel ? `Sold ${productsLabel}` : 'Sold Products')
    : (hasCustomLabel ? `Purchased & Capital ${productsLabel}` : 'Purchased & Capital Goods')
  const addProductPath = mode === 'sold'
    ? '/organisation/products/add-product?typeOfGood=soldProduct'
    : '/organisation/products/add-product'

  return (
    <PageOuterContainer>
      <PageHeading
        title={pageTitle}
        includeActionButton={true}
        actionButtonText={`Add ${screenLabelsContext.getLabel('product', { capitalize: 'first' })}`}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => { navigate(addProductPath) }}
        secondaryActionButtonHandler={() => { navigate('/settings/data-upload/import-data?dataType=products&start=upload') }}
        secondaryActionButtonText='Upload Data'
        secondaryActionButtonIcon={IconName.UploadOutlined}
        includeSecondaryActionButton={true}
        hideShowActionButton={hasNoGoodCategories}
        hideShowSecondaryActionButton={!userDetails?.roles.includes('admin') || hasNoGoodCategories}
      />
      {goodCatalogue &&
        <>
          {hasNoGoodCategories &&
            <Stack sx={{ mb: 2 }}>
              <InformationBox
                icon={IconName.ErrorOutline}
                text={`You are unable to create a ${screenLabelsContext.getLabel('product')} because you do not have any product categories`}
                actionText='Click here to create one'
                link={'/settings/company-settings/product-categories?openDialog=true'}
                marginTop={2}
              />
            </Stack>
          }
          {/* With no categories there is nothing to browse or search, so the info box above
              is the whole page until one is created. */}
          {!hasNoGoodCategories && <>
            <Stack direction='row' alignItems='center' justifyContent='flex-end' spacing={2} sx={{ mb: 2 }}>
              <Stack direction='row' alignItems='center' spacing={1}>
                <TextField
                  size='small'
                  placeholder={`Search ${screenLabelsContext.getLabel('product', { plural: true })}`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position='start'><SearchOutlined sx={{ color: 'text.secondary' }} /></InputAdornment>,
                  }}
                  sx={{ width: '240px' }}
                />
                <ToggleButtonGroup
                  value={view}
                  exclusive
                  onChange={(_, next: 'cards' | 'table' | null) => { if (next) setView(next) }}
                  orientation='horizontal'>
                  <ToggleButton value='cards' aria-label='Card view' sx={{ width: '50px', height: '40px', py: 0, textTransform: 'capitalize' }}>
                    <GridView />
                  </ToggleButton>
                  <ToggleButton value='table' aria-label='Table view' sx={{ width: '50px', height: '40px', py: 0, textTransform: 'none' }}>
                    <MenuOutlined />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Stack>
            {/* Fill the rest of the viewport (top bar + heading + toolbar + page padding is
                ~270px): with one product the grid and the category pane still run to the foot
                of the page instead of collapsing to a sliver. It is a minimum, so a long
                catalogue keeps growing past it. */}
            < Grid container spacing={4} sx={{ minHeight: 'calc(100vh - 270px)' }}>
              <Grid item lg={3} xs={3} sx={{ display: 'flex', flexDirection: 'column' }}>
                <GoodCatalogueTree
                  goodCatalogue={goodCatalogue}
                  handleItemSelectionToggle={handleItemSelectionToggle}
                  selectedCatalogueItemId={selectedCatalogueItemId ? selectedCatalogueItemId : ''}
                />
              </Grid>
              <Grid item lg={9} xs={9} >
                {capitalGoodsInCategory && capitalGoodsInCategory.length === 0 && mode === 'sold' &&
                  <InformationBox
                    icon={IconName.ErrorOutline}
                    text={`No sold ${screenLabelsContext.getLabel('product', { plural: true })} in this category yet. Add the ${screenLabelsContext.getLabel('product', { plural: true })} your organisation sells to build its sold-${screenLabelsContext.getLabel('product')} library, attaching EPDs and carbon footprint values to share with customers`}
                    actionText={hasNoGoodCategories ? undefined : 'Click here to create one'}
                    link={hasNoGoodCategories ? undefined : '/organisation/products/add-product?typeOfGood=soldProduct'}
                    marginTop={2}
                  />
                }
                {capitalGoodsInCategory && capitalGoodsInCategory.length === 0 && mode !== 'sold' &&
                  <InformationBox
                    icon={IconName.ErrorOutline}
                    text={`You do not have any ${screenLabelsContext.getLabel('product', { plural: true })} in this category`}
                    actionText={hasNoGoodCategories ? undefined : 'Click here to create one'}
                    link={hasNoGoodCategories ? undefined : '/organisation/products/add-product'}
                    marginTop={2}
                  />
                }
                {capitalGoodsInCategory && capitalGoodsInCategory.length > 0 && view === 'cards' &&
                  <>
                    < Grid container spacing={2}>
                      {capitalGoodsInCategory.map((good, index) => (
                        <Grid key={index} xs={12} sm={12} md={6} item lg={4} xl={4}  >
                          <Box sx={{ cursor: 'pointer' }} onClick={() => navigate(`/organisation/products/${good.id}`)}>
                            <ProductCatalogueCard good={good} showSoldMarker={false} />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                    <TablePagination
                      rowsPerPageOptions={[perPage]}
                      component="div"
                      count={totalRecords}
                      rowsPerPage={perPage}
                      page={inputPage}
                      onPageChange={handleGoodsPageChange}
                      onRowsPerPageChange={() => { }}
                      sx={{ mt: 2 }}
                    />
                  </>}
                {capitalGoodsInCategory && capitalGoodsInCategory.length > 0 && view === 'table' &&
                  <TableDisplay
                    headCells={tableHeadCells}
                    rows={tableRows}
                    selected={selectedTableRows}
                    setSelected={setSelectedTableRows}
                    handleSelected={(rowIds: string[]) => { if (rowIds[0]) navigate(`/organisation/products/${rowIds[0]}`) }}
                    inputPage={inputPage}
                    setInputPage={(value: React.SetStateAction<number>) => {
                      const page = typeof value === 'function' ? value(inputPage) : value
                      fetchGoods(selectedCatalogueItemId, page)
                    }}
                    totalRecords={totalRecords}
                    perPage={perPage}
                    setPerPage={setPerPage}
                  />}
              </Grid>
            </Grid>
          </>}
        </>}
    </PageOuterContainer >
  )
};
